import { useEffect, useCallback } from 'react';
import { useAndroidKeyboardDetection } from './useAndroidKeyboardDetection';

export const useConfirmFooterFocusVisibility = () => {
  // Android keyboard detection - show CTAs when keyboard closes
  const handleAndroidKeyboardClose = useCallback(() => {
    const isEditableElement = (el: EventTarget | null) => {
      if (!(el instanceof HTMLElement)) return false;
      const tag = el.tagName.toLowerCase();
      if (tag === 'textarea') {
        const textarea = el as HTMLTextAreaElement;
        return !textarea.readOnly && !textarea.disabled;
      }
      if (tag === 'input') {
        const input = el as HTMLInputElement;
        if (input.readOnly || input.disabled) return false;
        const type = (input.getAttribute('type') || 'text').toLowerCase();
        const textEntryTypes = new Set(['text', 'email', 'password', 'tel', 'number', 'search', 'url']);
        return textEntryTypes.has(type);
      }
      if (el.isContentEditable) return true;
      return false;
    };

    const active = document.activeElement;
    if (isEditableElement(active)) {
      document.body.classList.remove('cta-hidden');
    }
  }, []);

  useAndroidKeyboardDetection({
    enabled: true,
    onKeyboardClose: handleAndroidKeyboardClose,
  });

  useEffect(() => {
    const isMobileUserAgent = () => {
      const navAny = navigator as any;
      if (navAny?.userAgentData && typeof navAny.userAgentData.mobile === 'boolean') {
        return navAny.userAgentData.mobile;
      }
      const ua = (navigator.userAgent || '').toLowerCase();
      return /android|iphone|ipad|ipod|windows phone|iemobile|opera mini/.test(ua);
    };

    // Add Android class to body for CSS targeting (before mobile check for testing)
    // if (isAndroid()) {
    //   document.body.classList.add('android-device');
    //   document.body.classList.remove('cta-hidden');
    //   return;
    // }

    if (!isMobileUserAgent()) {
      document.body.classList.remove('cta-hidden');
      return;
    }

    const isEditableElement = (el: EventTarget | null) => {
      if (!(el instanceof HTMLElement)) return false;
      const tag = el.tagName.toLowerCase();
      if (tag === 'textarea') {
        const textarea = el as HTMLTextAreaElement;
        return !textarea.readOnly && !textarea.disabled;
      }

      if (tag === 'input') {
        const input = el as HTMLInputElement;
        if (input.readOnly || input.disabled) return false;

        const type = (input.getAttribute('type') || 'text').toLowerCase();
        const textEntryTypes = new Set(['text', 'email', 'password', 'tel', 'number', 'search', 'url']);
        return textEntryTypes.has(type);
      }
      if (el.isContentEditable) return true;
      return false;
    };

    let blurTimer: number | undefined;

    const updateHiddenState = () => {
      const active = document.activeElement;
      const shouldHide = isEditableElement(active);
      document.body.classList.toggle('cta-hidden', shouldHide);
    };

    const handleFocusIn = (e: FocusEvent) => {
      if (blurTimer) {
        window.clearTimeout(blurTimer);
        blurTimer = undefined;
      }

      if (isEditableElement(e.target)) {
        document.body.classList.add('cta-hidden');
      }
    };

    const handleFocusOut = () => {
      if (blurTimer) window.clearTimeout(blurTimer);
      blurTimer = window.setTimeout(() => {
        updateHiddenState();
        blurTimer = undefined;
      }, 150);
    };

    window.addEventListener('focusin', handleFocusIn);
    window.addEventListener('focusout', handleFocusOut);

    return () => {
      if (blurTimer) window.clearTimeout(blurTimer);
      window.removeEventListener('focusin', handleFocusIn);
      window.removeEventListener('focusout', handleFocusOut);
      document.body.classList.remove('cta-hidden');
    };
  }, []);
};
